'use-client';
import React, { useEffect, useImperativeHandle } from 'react';
import ResponsiveContainer from '@wix/thunderbolt-elements/components/ResponsiveContainer';
import {
  getDataAttributes,
  getAccessibilityAttributes,
} from '@wix/editor-elements-common-utils';
import cn from 'clsx';
import { SlideshowContextProvider } from '../../SlideshowContext';
import type { ISlideshowProps, ISlideshowImperativeActions } from '../../types';
import style from './styles/Slideshow.scss';

const Slideshow: React.ForwardRefRenderFunction<
  ISlideshowImperativeActions,
  ISlideshowProps
> = (props, ref) => {
  const {
    id,
    children,
    className,
    containerProps,
    containerRootClassName,
    currentIndex,
    setCurrentIndex,
    isPlaying,
    onPlay,
    onPause,
    onChange,
    onMouseEnter: onMouseEnterVelo,
    onMouseLeave: onMouseLeaveVelo,
    loop,
    navigatingForward,
    slideDuration,
    pauseOnHover,
  } = props;

  const { ariaAttributes, role, tabIndex } = getAccessibilityAttributes(props);

  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    setCurrentIndex,
  }));

  useEffect(() => {
    isPlaying ? onPlay?.({ type: 'onPlay' }) : onPause?.({ type: 'onPause' });
  }, [isPlaying, onPlay, onPause]);

  useEffect(() => {
    onChange?.({ type: 'onChange' });
  }, [currentIndex, onChange]);

  const onMouseEnter = (e: React.MouseEvent) => {
    setIsHovered(true);
    onMouseEnterVelo?.(e);
  };

  const onMouseLeave = (e: React.MouseEvent) => {
    setIsHovered(false);
    onMouseLeaveVelo?.(e);
  };

  return (
    <div
      id={id}
      ref={containerRef}
      {...getDataAttributes(props)}
      className={cn(containerRootClassName, className, style.slideshow)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      {...ariaAttributes}
      role={role || 'group'}
      tabIndex={tabIndex}
    >
      <SlideshowContextProvider
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        loop={loop}
        navigatingForward={navigatingForward}
        isPlaying={isPlaying}
        isHovered={isHovered}
        isFocused={isFocused}
        slideDuration={slideDuration}
        containerRef={containerRef}
        pauseOnHover={pauseOnHover}
      >
        <ResponsiveContainer {...containerProps}>
          {children}
        </ResponsiveContainer>
      </SlideshowContextProvider>
    </div>
  );
};

export default React.forwardRef(Slideshow);
